/**
 * useCalendarEvents Hook
 * React Query hook for fetching calendar events
 * Handles both grouped (month/week/year) and full (day/agenda) views
 */

import { useQuery } from '@tanstack/react-query';
import { fetchCalendarEvents } from '../api/calendarApi';
import { transformGroupedEvents, transformDayEvents, transformYearEvents } from '../utils/eventFormatters';

/**
 * Calendar events query hook
 *
 * @param {object} options - Query options
 * @param {string} options.view - Current calendar view
 * @param {object} options.dateRange - {start, end} date range
 * @param {object} options.filters - Applied filters
 * @param {array} options.selectedUsers - Selected user IDs
 * @param {string} options.timezone - User timezone
 * @param {boolean} options.enabled - Enable/disable query
 * @returns {object} React Query result
 */
export const useCalendarEvents = ({
  view,
  dateRange,
  filters = {},
  selectedUsers = [],
  timezone = 'America/Chicago',
  enabled = true,
  case_id = null,
  contact_id = null,
}) => {
  // Stable keys to avoid cache churn on object identity changes
  const startKey = dateRange?.start || null;
  const endKey = dateRange?.end || null;
  const selectedUsersKey = Array.isArray(selectedUsers) ? selectedUsers.join('|') : '';
  const filtersKey = filters ? JSON.stringify(filters) : '';

  // Query key includes stable primitives only
  const queryKey = ['calendar', 'events', view, startKey, endKey, filtersKey, selectedUsersKey, timezone, case_id, contact_id];

  // Only enable if we have a date range
  const isEnabled = Boolean(enabled && dateRange?.start && dateRange?.end);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { start, end } = dateRange;

      // Fetch based on view type
      const result = await fetchCalendarEvents(start, end, view, selectedUsers, timezone, case_id, contact_id, filters);
      if (view === 'multiMonthYear') {
        return transformYearEvents(result?.data, timezone);
      } else if (view === 'dayGridMonth') {
        return transformGroupedEvents(result?.data, timezone, start, end);
      } else {
        return transformDayEvents(result?.data, timezone, start, end);
      }
    },
    enabled: isEnabled,
    staleTime: 1 * 60 * 1000, // 1 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (replaces cacheTime in v4+)
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true, // Always refetch when component mounts
    retry: 1, // Retry failed requests once
  });

  return {
    ...query,
    events: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export default useCalendarEvents;
